<template>
  <div :class="$attrs.class">
    <div class="tw-flex tw-flex-col tw-grow tw-overflow-hidden md:tw-rounded-3xl xl:rounded-none 2xl:tw-rounded-3xl md:tw-shadow-lg xl:tw-shadow-none 2x:tw-shadow-lg tw-bg-white tw-group tw-w-full tw-relative">
      <template v-if="state.loadingProduct">
        
        <div class="tw-w-full">
          <SkeletonLoader class="tw-w-1/2 tw-h-48 tw-mx-auto tw-mb-4 tw-mt-8" />
        </div>

        <div class="tw-w-full tw-px-8">
          <SkeletonLoader class="tw-w-1/2 tw-h-10 tw-mx-auto tw-mb-6" />
          <SkeletonLoader class="tw-w-1/3 tw-h-6 tw-mx-auto tw-mb-2" />
          <SkeletonLoader class="tw-w-4/5 tw-h-16 tw-mx-auto tw-mb-8" />

          <div class="tw-mt-8 tw-pt-8 tw-border-t tw-border-gray-500 tw-w-full">
            <SkeletonLoader type="circle" class="tw-w-full tw-h-16 tw-mx-auto tw-mb-8 tw-rounded-full" />
            <SkeletonLoader type="circle" class="tw-w-4/5 tw-h-16 tw-mx-auto tw-mb-8 tw-rounded-full" />
          </div>
        </div>
        
      </template>

      <template v-else>
        <div class="tw-absolute tw-top-2 tw-right-2 tw-z-10 tw-rounded-full tw-px-4 tw-text-white tw-bg-orange-medium hover:tw-bg-black tw-transition-all tw-duration-300 tw-cursor-pointer tw--translate-x-px" @click="handleDetails" aria-label="More Details">
          <span class="tw-text-2xl fa fa-info"></span>
        </div>

        <div class="tw-mb-4">
          <div class="tw-mb-4" v-if="state.product && productHero">
            <img 
              :alt="state.product.name+' image'"
              class="tw-w-full"
              :src="productHero">
          </div>

          <div class="tw-px-8 tw-pb-8 md:tw-pb-0">
            

            <h2 class="tw-text-3xl tw-font-bold tw-mb-0 tw-text-center" v-if="state.product">{{state.product.name}}</h2>
            <p class="tw-mb-2 tw-text-xl tw-text-center" v-if="state.product && state.product.description">
               <span v-if="state.product && state.product.cost"> ${{state.product.cost.toLocaleString("en-US", {minimumFractionDigits: 2})}} each</span> 
            </p>
            <p class="hidden tw-mb-8 tw-text-lg" v-if="state.product && state.product.description">
              {{state.product.description}}
            </p>
            
            <div v-if="state.product" class="tw-pt-4">
              <template 
                v-for="(optionGroup , index) in state.product.optiongroups" 
                :key="'og-'+(optionGroup as OptionGroupInterface)!.id"
                >
              
                <OptionGroup
                  :class="['tw-text-center tw-mb-8', {'tw-pt-0 md:tw-mt-4': index == 0}, {'tw-mt-4': optionGroup.parentId == null && index != 0 }]"
                  :selectedOptions="state.selectedOptions"
                  :optionGroup="optionGroup"
                  :isFirst="index == 0"
                  :modularNutritionData="modularNutritionData! as Boolean"
                  :layoutType="'condensed'"
                  @optionAdded="selectOption"
                  @optionRemoved="removeOption"
                />

              </template>

              <div class="tw-flex-1 tw-flex tw-mb-4 tw-mt-12 tw-pt-12 tw-border-t tw-border-gray-500">
                <div class="tw-flex md:tw-flex-1 tw-items-center tw-relative tw-w-96 md:tw-w-auto tw-flex-grow-0 md:tw-flex-grow tw-mx-auto">
                  <input 
                  v-model="state.quantity"
                  min="1"
                  increment="1"
                  class="tw-w-full tw-text-3xl tw-max-w-96 tw-text-center tw-px-12 tw-py-3 tw-border focus:tw-outline-none tw-border-gray-400 focus:tw-border-orange-medium focus:tw-ring-0 tw-rounded-full"
                  type="number">
                  <button
                    @click="decrementQuantity"
                    class="tw-px-6 tw-text-3xl hover:tw-text-orange-medium tw-transition-all tw-font-bold tw-text-black tw-absolute tw-left-0">
                    &mdash;
                  </button>

                  <button
                    @click="incrementQuantity"
                    class="tw-px-6 tw-text-3xl hover:tw-text-orange-medium tw-transition-all tw-font-bold tw-text-black tw-absolute tw-right-0 ">
                    +
                  </button>
                </div>
              </div>
              <div>
                <TwButton 
                  cssClass="tw-bg-orange-medium hover:tw-bg-orange-dark tw-border-orange-medium tw-border tw-w-full tw-rounded-full tw-text-white tw-text-2xl tw-uppercase tw-font-bold tw-px-6 tw-py-4 tw-transition-all tw-duration-200 tw-text-3xl" 
                  @emit-click="addItem" 
                  :isLoading="state.addingToBag"
                  :isDisabled="props.disabled" 
                  :buttonText="addItemText"/>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>


<script setup lang="ts">
  import {
    reactive, computed, onMounted, ref, watch
  } from 'vue';
  import { getCurrentInstance } from "vue";
  import { useRoute, useRouter } from 'vue-router';

  // Data
  import { useBasketStore } from '../../stores/basket';
  import { useCateringOrderStore } from '../../stores/catering_order';
  import { useMenuStore } from '../../stores/menu';
  import { useUserStore } from '../../stores/user';
  import { useFlashMessageStore } from '../../stores/flashMessages';

  import Product from '../../classes/Product';
  import type MenuCategory from '../../classes/MenuCategory'
  import type BasketProduct from '../../classes/BasketProduct';
  import type BasketProductChoice from '../../types/BasketProductChoice';
  import { plainToClass } from 'class-transformer';
  import OptionGroup from './_OptionGroup.vue';
  import Addons from './_Addons.vue';
  import Dietary from './_Dietary.vue';
  import TwCollapse from '../../components/TwCollapse.vue';
  import type OptionGroupInterface from '../../types/OptionGroup';
  import type OptionInterface from '../../types/Option';
  import type ChoiceCustomField from '../../types/ChoiceCustomField';
  import type FlashMessage from '../../types/FlashMessage';
  import type IdActive from '../../types/IdActive';

  import SkeletonLoader from '../../components/SkeletonLoader.vue';
  import TwButton from '../../components/TwButton.vue';
  import ProgressBar from '../../components/ProgressBar.vue';
  import NutritionData from '../../classes/NutritionData';

  const basketStore = useBasketStore();
  const cateringOrderStore = useCateringOrderStore();
  const menuStore = useMenuStore();
  const userStore = useUserStore();
  const flashMessageStore = useFlashMessageStore();
  
  const route = useRoute();
  const router = useRouter();

  const props = defineProps<{ product: Product, disabled: boolean, assistanceLevel: number, quantity: number }>();
  const emit = defineEmits(['handleProductQuickAdd', 'handleDetailsModal'])
  const state = reactive({
     product: null as (Product | null),
     basketProduct: null as (BasketProduct | null),
     selectedOptions: new Map<number, number>(),
     includedIngredients: new Map<string, number>(),
     quantity: 1,
     addingToBag: false,
     originalAddons: [] as Product[],
     addons: [] as Product[],
     selectedAddons: [] as Product[],
     loadingProduct: true,
     debug: false,
     parentCategory: undefined as (MenuCategory | undefined),
     productOptionDiets: undefined as (Map<number, string[]> | undefined),
     productOptionAllergens: undefined as (Map<number, string[]> | undefined),
     allergensUnknown: true,
     dietsUnknown: true,
     rootOptions: new Map<number, number[]>(),
     groupDietaryOpen: false,
   })

  // Runs through the product data and sets up default selection states and flattens as needed

  const initializeProduct = ((product: Product) => {
    let vm = this;
    const prod = product;

    let ogs = [] as OptionGroupInterface[];

    if (product.optiongroups && product.optiongroups.length > 0) {
      product.optiongroups.forEach((og) => {
        const desc = og.description.toLowerCase()
        if (og.description.toLowerCase() == "included ingredients") {
          og.options.forEach((option: OptionInterface) => {
            state.includedIngredients.set(option.name.toLowerCase().replace('no ', ''), option.id)
          });
        }
        // Todo: Map extra proteins to regular

        const fog = flattenedOptionGroup(og);
        const preselectedGroups = preSelectOptionsForGroup(fog);
        ogs = ogs.concat(preselectedGroups);
      })

      // Set root option group ids
      let rootId = undefined as (number | undefined);
      let rootOptions = new Map<number, number[]>()
      ogs.forEach((og) => {
        if (og.parentId == null) {
          rootId = og.id;
          rootOptions.set(og.id, []);
        } else if (rootId) {
          og.rootGroupId = rootId;
        }

        if (og.options && rootId && rootOptions.get(rootId)) {
          og.options.forEach((opt) => {
            rootOptions.get(rootId!)!.push(opt.id);
          })
        }
      })
      state.rootOptions = rootOptions;
    }


    if (prod.custom_data && prod.custom_data.addon_products) {
      let prods = JSON.parse(prod.custom_data.addon_products);
      // Loop over the addons and get the full products
      Object.entries(prods).forEach((addonArr: Array<any>) => {
        menuStore.getProductByChainId(addonArr[0]).then((addonProduct: Product) => {
          if (addonProduct) {
            addonProduct.quantity = addonArr[1];
            state.originalAddons.push(addonProduct);
            state.addons.push(addonProduct);
          }
        })
      })
    }

    if (state.basketProduct) {
      prod.optiongroups = preSelectBasketChoices(ogs);
    } else {
      prod.optiongroups = ogs;
    }

    state.product = prod;

    state.allergensUnknown = product.allergens == null || product.allergens == undefined
    state.dietsUnknown = product.diets == null || product.diets == undefined

    state.productOptionDiets = prod.optionsNutrition("dietary")
    state.productOptionAllergens = prod.optionsNutrition("allergens")

    setProductBreadcrumbCategory()

    state.loadingProduct = false;
  })

  const preSelectOptionsForGroup = ((ogs: OptionGroupInterface[]): OptionGroupInterface[] => {
    let isBasketProduct = false

    ogs.forEach((og) => {
      og.options.forEach((option: OptionInterface) => {
        let shouldSelect = false;
        // Check if the option is a default
        if (!isBasketProduct && option.isdefault && (option.inverseId == null || option.inverseId == undefined)) {
          shouldSelect = true;
        }

        if (shouldSelect) {
          // option.isSelected = true;
          if (!state.selectedOptions.has(option.id)) {
            if (option.quantity) {
              state.selectedOptions.set(option.id, option.quantity);
            } else {
              state.selectedOptions.set(option.id, 1);
            }
          } // Else its already selected?
        }
      })
    })

    return ogs;
  })

  const preSelectBasketChoices = ((ogs: OptionGroupInterface[]): OptionGroupInterface[] => {
    const mappedBasketChoices = state.basketProduct!.choices.map((choice: BasketProductChoice) => {
      return choice.optionid
    })
    // We'll loop over the choices and figure out selections
    ogs.forEach((og: OptionGroupInterface) => {
      if ((og.description.toLowerCase() != "included ingredients") && og.options) {
        og.options.forEach((opt: OptionInterface) => {
          if (mappedBasketChoices.includes(opt.id) || mappedBasketChoices.includes(opt.inverseId) ) {

            // We have to get the exact basket choice to confirm option quantity
            let bpchoice = state.basketProduct!.choices.find((choice: BasketProductChoice) => {
              if (opt.inverseId != null && opt.inverseId != undefined) {
                return choice.optionid == opt.inverseId;
              } else {
                return choice.optionid == opt.id;
              }
            })

            if (bpchoice) {
              if (opt.inverseId != null && opt.inverseId != undefined) {
                opt.isSelected = false;
                opt.quantity = 0;
                if (state.selectedOptions.has(opt.id)) {
                  // Set qty 0 and loop back over to set the basket choice qty
                  state.selectedOptions.set(opt.id, 0)
                } 
                for(let i = 0; i < bpchoice.quantity; i++){
                  selectOption(opt)
                }
              } else {
                opt.isSelected = true;
                opt.quantity = bpchoice.quantity;
                if (state.selectedOptions.has(opt.id)) {
                  // Set qty 0 and loop back over to set the basket choice qty
                  state.selectedOptions.set(opt.id, 0)
                } 

                for(let i = 0; i < bpchoice.quantity; i++){
                  selectOption(opt)
                }
              }
            }
          }
        })
      }
    })

    return ogs
  })

  // Given an option group, will loop through nested options to look for nested option groups
  // Returns an array of all option groups nested under the option group
  const flattenedOptionGroup = ((og: OptionGroupInterface): OptionGroupInterface[] => {
    let isBasketProduct = false;
    let flattenedOgs = [og] as OptionGroupInterface[];
    if (og.options && og.options.length > 0) {
      og.options.forEach((opt: OptionInterface) => {
        if (state.includedIngredients) {
          const isIncluded = state.includedIngredients.has(opt.name.toLowerCase());
          if (isIncluded) {
            opt.isSelected = isIncluded;
            opt.inverseId = state.includedIngredients.get(opt.name.toLowerCase())!
          } else if (!isBasketProduct && opt.isdefault && !isBasketProduct) {
            opt.isSelected = true
          }
        }

        if (opt.optiongroups && opt.optiongroups.length > 0) {
          opt.optiongroups.forEach((og2: OptionGroupInterface) => {
            og2.parentId = opt.id;

            flattenedOgs = flattenedOgs.concat(flattenedOptionGroup(og2));
          })
        }
      })
    }

    return flattenedOgs;
  })

  const selectOption = ((option: OptionInterface) => {
    if (option.inverseId != null || option.inverseId != undefined) {
      if (state.selectedOptions.has(option.inverseId)) {
        state.selectedOptions.set(option.inverseId, (state.selectedOptions.get(option.inverseId)! + 1))
      } else {
        state.selectedOptions.set(option.inverseId, 1)
      }
    } else {
      if (state.selectedOptions.has(option.id)) {
        state.selectedOptions.set(option.id, (state.selectedOptions.get(option.id)! + 1))
      } else {
        state.selectedOptions.set(option.id, 1)
      }
    }

  })

  const removeOption = ((option: OptionInterface) => {
    if (option.inverseId != null || option.inverseId != undefined) {
      if (state.selectedOptions.has(option.inverseId) && state.selectedOptions.get(option.inverseId)! > 1) {
        state.selectedOptions.set(option.inverseId, (state.selectedOptions.get(option.inverseId)! - 1))
      } else {
        state.selectedOptions.delete(option.inverseId);
      }
    } else {
      if (state.selectedOptions.has(option.id) && state.selectedOptions.get(option.id)! > 1) {
        state.selectedOptions.set(option.id, (state.selectedOptions.get(option.id)! - 1))
      } else {
        state.selectedOptions.delete(option.id);
        deselectChildOptions(option);
      }
    }
  })

  const toggleAddon =  ((addon: Product) => {
    const selectedIndex = state.selectedAddons.map(sel => sel.id).indexOf(addon.id);
    if (selectedIndex > -1) {
      const idx = selectedIndex;
      state.selectedAddons.splice(idx, 1);
    } else {
      state.selectedAddons.push(addon)
    }
  })

  const deselectChildOptions = ((option: OptionInterface) => {
    if (option.optiongroups && option.optiongroups.length > 0) {
      option.optiongroups.forEach((og: OptionGroupInterface) => {
        if (og.options && og.options.length > 0) {
          og.options.forEach((opt: OptionInterface) => {
            if (opt.isSelected) {
              if (opt.quantity > 1) {
                for(let i=0; i < opt.quantity; i++) {
                  removeOption(opt);
                }
              } else {
                removeOption(opt);
              }
              opt.isSelected = false;
            }
          })
        }
      })
    }
  })

  const addItem = () => {
    if (state.product) {
      state.addingToBag = true;
      emit('handleProductQuickAdd', {id: props.product.id.toString(), active: true} as IdActive)
      let options = [] as number[];
      let choicecustomfields = [] as ChoiceCustomField[];
      if (state.selectedOptions) {
        // Loop over options to add them with quantities if necessary
        state.selectedOptions.forEach((qty,id) => {
          if (qty > 1) {
            options.push(id);
            choicecustomfields.push({choiceid: id, quantity: qty} as ChoiceCustomField)
          } else {
            options.push(id);
          }
        })
      }

      // Kick off the product add call, if it succeeds we can try to also add the addon products
      basketStore.addProduct(state.product.id!.toString(), state.quantity, "", options, choicecustomfields, undefined).then(() => {
        if (state.selectedAddons && state.selectedAddons.length > 0) {
          let addonsAdded = 0;
          state.selectedAddons.forEach((addon: Product) => {
            basketStore.addProduct(addon.id!.toString(), addon.quantity, "", [], [], undefined).then(() => {
              addonsAdded += 1;
              if (addonsAdded == state.selectedAddons.length) {
                completeProductAdd()
              }
            })
          })
          
        } else {
          completeProductAdd()
        }
      }).catch((error: Error) => {
        popError(error.message, "Error Adding Item");
        state.addingToBag = false;
        emit('handleProductQuickAdd', {id: props.product.id.toString(), active: false} as IdActive)
      })
    } else {
      // We have problems
      popError("Item could not be added. Please make sure all required selections have been made and try again.", "Error Adding Item");
      state.addingToBag = false;
      emit('handleProductQuickAdd', {id: props.product.id.toString(), active: false} as IdActive)
    }
  };

  const popError = (error: string, title: string) => {
    const message = {
        id: Math.round(Date.now() / 1000),
        messageType: "danger",
        text: error,
        headline: title,
        delay: 0, // ms
        icon: "",
        cssClass: "",
    } as FlashMessage;
    flashMessageStore.addMessage(message);
  }

  const completeProductAdd = (() => {
    flashMessageStore.dismissErrors();
    const message = {
        id: Math.round(Date.now() / 1000),
        messageType: "success",
        text: "Product has been added to bag",
        headline: "",
        delay: 5000, // ms
        icon: "",
        cssClass: "",
    } as FlashMessage;
    flashMessageStore.addMessage(message);

    state.addingToBag = false;
    emit('handleProductQuickAdd', {id: props.product.id.toString(), active: false} as IdActive)
    state.product!.quantity = 1
    // router.push('/menu')
  })

  const decrementQuantity = () => {
    state.quantity = Math.max(state.quantity -= 1, 1);
  };

  const incrementQuantity = () => {
    state.quantity = Math.min(state.quantity += 1, 99);
  };

  watch(() => state.quantity, (newQty, oldQty) =>  {
    updateAddonQuantity(newQty);
  })

  const updateAddonQuantity = ((updatedQuantity: number) => {
    let dupedAddons = JSON.parse(JSON.stringify(state.originalAddons)) as Product[]

    dupedAddons.forEach((addon: Product) => {
      addon.quantity = Math.round((addon.quantity * updatedQuantity));
      if (state.selectedAddons) {
        // Check to see if this addon is selected, and up the selected addon to match quantity
        const match = state.selectedAddons.find((selAddon: Product) => {
          return selAddon.id == addon.id
        })

        if (match) {
          match.quantity = addon.quantity;
        }
      }
    })

    state.addons = dupedAddons;
  })

  const setProductBreadcrumbCategory = () => {
    let category = undefined as (MenuCategory | undefined);

    // We'll have to search through the menu to find the category this item is in
    menuStore.menu.forEach((cat: MenuCategory) => {
      if (cat.products && cat.products.length > 0) {
        const matchedProduct = cat.products.find((prod: Product) => {
          return prod.id == state.product!.id;
        })

        if (matchedProduct) {
          category = cat;
        }
      }
    })

    state.parentCategory = category;
  }

  const currentPrice = computed(() => {
    let calculatedCost = 0.00;
    if (state.product) {
      calculatedCost += state.product.cost! as number
    
      // Loop over all the options and get their cost
      state.product!.optiongroups!.forEach((og: OptionGroupInterface) => {
        og.options.forEach((opt: OptionInterface) => {
          if (opt.isSelected) {
            if (opt.cost > 0) {
              calculatedCost += (opt.cost * (opt.quantity > 0 ? opt.quantity : 1));
            }
          }        
        })
      })
    }

    if (state.selectedAddons && state.selectedAddons.length > 0) {
      state.selectedAddons.forEach((addon: Product) => {
        calculatedCost += (addon.cost! as number * addon.quantity);
      })
    }

    return calculatedCost * state.quantity;
  })

  const currentAllergens = computed(() => {
    let allergens = [] as string[];
    // Loop over current selected options to determine the dietary makeup of the product
    if (state!.product!.allergens!) {
      allergens = state!.product!.allergens!
      state!.selectedOptions.forEach((count, option) => {
        if (state!.productOptionAllergens!.has(option)) {
          if (state.productOptionAllergens!.get(option)) {
            allergens = allergens.concat(state!.productOptionAllergens!.get(option)!)
          }
        }
      })
    }
    return [...new Set(allergens)].map((allergy) => { 
      return NutritionData.allergens[allergy as keyof typeof NutritionData.allergens]
    })
  })

  const currentDietary = computed(() => {
    let diets = [] as string[];
    // Loop over current selected options to determine the dietary makeup of the product
    if (state!.product!.diets!) {
      diets = state!.product!.diets!
      state!.selectedOptions.forEach((count, option) => {
        if (state!.productOptionDiets!.has(option)) {
          // only keep the parent diet if the option has the same diet
          if (state!.productOptionDiets!.get(option)) {
            diets = diets.filter(d => state!.productOptionDiets!.get(option)!.includes(d))
          } else {
            diets = []
          }
        }
      })
    }
    
    return [...new Set(diets)].map((diet) => {
      if (diet) {
        return NutritionData.diets[diet as keyof typeof NutritionData.diets]
      }
    })
  })

  const productHero = computed(() => {
    let img = null

    if (state.product && state.product.images) {
      const imageByKey = state.product.getImageByKey("product_web_block")
      if (imageByKey) {
        img = state!.product!.getImageByKey("product_web_block")!.url
      } else {
        img = 'https://mypiada-assets.s3.amazonaws.com/static_assets/scooter-placeholder.jpg';
      }
    }

    return img
  })

  // Count selected options and how many guests they feed
  const optionsFeed = computed(() => {
    let feeds = 0;
    // TODO: Add this back when the basket can handle options having a feeds property
    // let optionFeedsMap = new Map<number, number>();

    // if (state.product && state.product.optiongroups) {
    //   state.product.optiongroups.forEach((og: OptionGroupInterface) => {
    //     if (og.options && og.options.length > 0) {
    //       og.options.forEach((opt: OptionInterface) => {
    //         if (opt.feeds && opt.feeds != 0 && opt.isSelected) {
    //           if (opt.quantity && opt.quantity > 1) {
    //             feeds += Math.round((opt.feeds * opt.quantity))
    //           } else {
    //             feeds += opt.feeds
    //           }
    //         }
    //       })
    //     }
    //   })
    // }

    return feeds;
  })

  const metaFeeds = computed(() => {
    let feeds = 0

    let count = 0;
    let lightFeeds = 0;
    let regularFeeds = 0;
    let hungryFeeds = 0;
    let lightPct = 0;
    let regularPct = 0;
    let hungryPct = 0;
    let totalGuests = 0;

    if (cateringOrderStore.cateringOrder) {
      totalGuests = cateringOrderStore.cateringOrder.guest_count;
    }

    // console.log(guestHungerArray.value, totalGuests);

    if (totalGuests && totalGuests > 0) {
      lightPct = (guestHungerArray.value.at(0)!) / totalGuests;
      regularPct = (guestHungerArray.value.at(1)!) / totalGuests;
      hungryPct = (guestHungerArray.value.at(2)!) / totalGuests;
    } else {
      regularPct = 1;
    }

    // console.log('percents', lightPct, regularPct, hungryPct);


    if (state.product && state.product.feeds) {
      lightFeeds += (state.product.feeds.at(0)!);
      regularFeeds += (state.product.feeds.at(1)!);
      hungryFeeds += (state.product.feeds.at(2)!);
    }

    // console.log('feeds count', lightFeeds, regularFeeds, hungryFeeds)

    let feedsLight = lightFeeds * lightPct;
    let feedsRegular = regularFeeds * regularPct;
    let feedsHungry = hungryFeeds * hungryPct;

    // console.log(feedsLight, feedsRegular, feedsHungry, " = ", (feedsLight + feedsRegular + feedsHungry));

    let feedsAmount = Math.round(feedsLight + feedsRegular + feedsHungry);
    count += feedsAmount * state.quantity;

    return count;
  })

  const guestHungerArray = computed(() => {
    if (cateringOrderStore.cateringOrder) {
      return [cateringOrderStore.cateringOrder!.light_eaters, cateringOrderStore.cateringOrder!.regular_eaters, cateringOrderStore.cateringOrder!.hungry_eaters];
    } else {
      return [0,0,0];
    }
  })

  const baselineCovered = computed(() => {
    if (cateringOrderStore.cateringOrder!.guest_count && basketStore.basket) {
      let currentlyFeeds = basketStore.basket.basketFeeds(guestHungerArray.value, cateringOrderStore.cateringOrder!.guest_count, undefined);

      return Math.min(((currentlyFeeds/cateringOrderStore.cateringOrder!.guest_count)*100), 100);
    } else {
      return 0;
    }
  })

  const percentageAccountedFor = computed(() => {
    if (cateringOrderStore.cateringOrder && cateringOrderStore.cateringOrder.guest_count == undefined || cateringOrderStore.cateringOrder && cateringOrderStore.cateringOrder!.guest_count < 1)  {
      return 100;
    } else if (metaFeeds.value && cateringOrderStore.cateringOrder!.guest_count) {
      return Math.min(((metaFeeds.value/cateringOrderStore.cateringOrder!.guest_count)*100), 100);
    } else {
      return 0;
    }
  })

  const totalFed = computed(() => {
    let count = 0;
    let baskFeeds = null;
    if (basketStore.basket) {
      baskFeeds = basketStore.basket!.basketFeeds(guestHungerArray.value, cateringOrderStore.cateringOrder!.guest_count, undefined);
    }

    if (metaFeeds) {
      count += metaFeeds.value!
    }
    
    if (baskFeeds) {
      count += baskFeeds
    }

    return count;
  })

  const modularNutritionData = computed(() => {
    return state.product && state.product.quick_combo_item == true;
  })

  const visibleNestedGroupIds = computed(() => {
    let groupIds = new Map() as (Map<number, number[]>);
    let currentRoot = undefined as (number | undefined);

    if (visibleOptionGroups.value && visibleOptionGroups.value.length > 0) {
      visibleOptionGroups.value.forEach((og) => {
        if (og.parentId == null) {
          currentRoot = og.id;
          groupIds!.set(og.id, []);
        }
        if (currentRoot && groupIds && groupIds!.get(currentRoot)) {
          groupIds!.get(currentRoot)!.push(og.id);
        }
      })
    }

    return groupIds;
  })

  const visibleOptionGroups = computed(() => {
    let ogs = [] as OptionGroupInterface[]

    if (state.product && state.product.optiongroups) {
      state.product!.optiongroups.forEach((og) => {
        let visible = true;

        if (og.description.toLowerCase() == "included ingredients") {
          visible = false
        } else if (og.parentId != null && og.parentId != undefined) {
          // If the option group has a parent, that parent needs to either be selected or a inverted unselected option
          // TODO: Check for inverted unselected state on aprent
          visible = (state.selectedOptions.has(og.parentId));
        }

        if (visible) {
          ogs.push(og);
        }
      })
    }

    return ogs;
  })

  const calculatedSelectionsInGroup = ((rootId: number) => {
    // Get all the selected options that are in the root group and nested
    let allOptions = state.rootOptions.get(rootId);
    let selectedOpts = Array.from(state.selectedOptions.keys());
    let selectedGroupOptions = [] as number[];
    if (allOptions && allOptions.length > 0 && selectedOpts) {
      selectedGroupOptions = allOptions.filter(opt => selectedOpts.includes(opt));
    }

    return selectedGroupOptions;
  })

  const calculatedAllergensForGroups = computed(() => {
    let rootIds = Array.from(state.rootOptions.keys());
    let allergensPerGroup = new Map() as (Map<number, string[]>);
    if (rootIds) {
      rootIds.forEach((rootId) => {
        allergensPerGroup.set(rootId, []);

        if (calculatedSelectionsInGroup(rootId)) {
          let allergens = [] as string[];    
          calculatedSelectionsInGroup(rootId).forEach((optId) => {
            if (state!.productOptionAllergens!.has(optId)) {
              if (state.productOptionAllergens!.get(optId)) {
                allergens = allergens.concat(state!.productOptionAllergens!.get(optId)!)
              }
            }
          })

          if (allergensPerGroup.get(rootId)) {
            [...new Set(allergens)].map((allergy) => { 
              allergensPerGroup.get(rootId)!.push(NutritionData.allergens[allergy as keyof typeof NutritionData.allergens]);
            })
          }
        }
      })
    }

    return allergensPerGroup;
  })

  const calculatedDietsForGroup = computed(() => {
   let rootIds = Array.from(state.rootOptions.keys());
    let dietsPerGroup = new Map() as (Map<number, string[]>);
    let dietsUnknown = false;
    if (rootIds) {
      rootIds.forEach((rootId) => {
        dietsPerGroup.set(rootId, []);
        let diets = Object.keys(NutritionData.diets) as string[]; // I guess start from all of the diets

        if (calculatedSelectionsInGroup(rootId)) {
          calculatedSelectionsInGroup(rootId).forEach((optId) => {
            if (state!.productOptionDiets!.has(optId)) {
              // only keep the parent diet if the option has the same diet
              if (state!.productOptionDiets!.get(optId)) {
                diets = diets.filter(d => state!.productOptionDiets!.get(optId)!.includes(d))
              } else {
                diets = []
              }
            } else {
              dietsUnknown = true
            }
          })
        
          if (dietsPerGroup.get(rootId)) {
            [...new Set(diets)].map((diet) => {
              if (diet) {
                dietsPerGroup.get(rootId)!.push(NutritionData.diets[diet as keyof typeof NutritionData.diets]);
              }
            })
          }
        }
      })
    }

    return dietsUnknown ? new Map() as (Map<number, string[]>) : dietsPerGroup;
  })

  const shouldDisplayGroupNestedNutrition = ((id: number, rootId: number) => {
    let show = false;

    if (visibleNestedGroupIds.value) {
      let groups = visibleNestedGroupIds.value.get(rootId);
      if (groups && groups.length > 0) {
        show = groups[groups.length - 1] == id;
      }
    }

    return show;
  })

  const beginCustomizeView = (() => {
    menuStore.getProduct(parseFloat(props.product.id)).then((product: Product) => {
      initializeProduct(plainToClass(Product, (product as object)) as Product)
    }) // menuStore.fetchProduct(parseFloat(route.params.id.toString()))
  })

  const addItemText = computed(() => {
    let txt = state.basketProduct != null ? 'Update Item' : 'Add To Order';
    if (state.product && state.product.cost) {
      txt += ' - $' + (currentPrice.value.toLocaleString("en-US", {minimumFractionDigits: 2}));
    }
    return txt;
  })

  onMounted(() => {
    beginCustomizeView()
  })

  const handleDetails = (event: Event) => {
    props.product.quantity = state.quantity;
    emit('handleDetailsModal', props.product)
  }

</script>

<style>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button,
input[type='number'] {
  -webkit-appearance: none;
  margin: 0;
  -moz-appearance: textfield !important;
}
</style>